import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

interface MeetingNotification {
  id: string;
  title: string;
  scheduled_at: string;
  deal_id?: string | null;
  lead_id?: string | null;
  contact_id?: string | null;
  location?: string | null;
  meeting_url?: string | null;
  google_calendar_event_id?: string | null;
  google_calendar_html_link?: string | null;
}

export function useMeetingNotifications() {
  const [notifications, setNotifications] = useState<MeetingNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [appUserId, setAppUserId] = useState<string | null>(null);
  const { user } = useAuth();

  // Resolve auth user to app_user_id once
  useEffect(() => {
    if (!user?.id) {
      setAppUserId(null);
      return;
    }
    const resolve = async () => {
      const { data } = await supabase
        .from('app_users')
        .select('id')
        .eq('auth_user_id', user.id)
        .maybeSingle();
      setAppUserId(data?.id ?? null);
    };
    resolve();
  }, [user?.id]);

  const loadNotifications = async () => {
    if (!appUserId) return;

    try {
      const now = new Date();
      const reminderTime = new Date(now.getTime() + 5 * 60000); // 5 minutos antes

      const { data, error } = await supabase
        .from('calls')
        .select('*')
        .eq('channel', 'video')
        .or(`created_by.eq.${appUserId},handled_by_app_user_id.eq.${appUserId}`)
        .gte('scheduled_for', now.toISOString())
        .lte('scheduled_for', reminderTime.toISOString());

      if (error) {
        console.error('Error loading meeting notifications:', error);
        return;
      }

      const meetingsData: any[] = data || [];

      // Filtrar por status no cliente
      const filteredMeetings: MeetingNotification[] = meetingsData
        .filter((m: any) => m.status === 'scheduled' || m.status === 'pending')
        .map((m: any) => ({
          id: m.id,
          title: m.subject || m.title || 'Meeting',
          scheduled_at: m.scheduled_for,
          deal_id: m.deal_id,
          lead_id: m.lead_id,
          contact_id: m.contact_id,
          location: (m.metadata as any)?.location,
          meeting_url: m.meeting_url,
          google_calendar_event_id: m.google_calendar_event_id,
          google_calendar_html_link: m.google_calendar_html_link,
        }));

      setNotifications(filteredMeetings);
      setUnreadCount(filteredMeetings.length);
    } catch (error) {
      console.error('Erro ao carregar notificações de meetings:', error);
    }
  };

  const markAsRead = (meetingId: string) => {
    setNotifications(prev => prev.filter(n => n.id !== meetingId));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const removeNotification = (meetingId: string) => {
    markAsRead(meetingId);
  };

  useEffect(() => {
    if (!appUserId) return;

    loadNotifications();

    const interval = setInterval(() => {
      loadNotifications();
    }, 60000);

    const channel = supabase
      .channel('meeting-notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'calls',
        },
        (payload) => {
          if ((payload.new as any)?.channel === 'video') {
            loadNotifications();
          }
        }
      )
      .subscribe();

    return () => {
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, [appUserId]);

  return {
    notifications,
    unreadCount,
    markAsRead,
    removeNotification,
    refresh: loadNotifications
  };
}
